<!--
  @component Brush controls to change the x-axis domain.

  - `enableBrushZoom`: Touch and drag to highlight a rectangular interval of the x-axis.
  - `enableWheelZoom`: Scroll up or down to zoom in or out, in the x-direction. For touch devices,
    also scroll left or right, or pinch to zoom.
-->

<script>
  import { createEventDispatcher, getContext } from "svelte";

  import BrushArea from "./BrushArea.svelte";

  const { padding, height, width, xScale } = getContext("LayerCake");

  /** @type {Boolean} Enables brushing the x-axis to zoom in. */
  export let enableBrushZoom = false;

  /** @type {Boolean} Enables scrolling or pinching to zoom in or out. */
  export let enableWheelZoom = false;

  const dispatch = createEventDispatcher();

  let brushPixels = undefined; // [startPx, endPx]
  let brushRange = undefined; // [startX, endX]

  const getPx = (event) => event.offsetX - $padding.left;

  function handleMouseDown(event) {
    if (enableBrushZoom) {
      const px = getPx(event);
      const x = $xScale.invert(px);
      brushPixels = [px, px];
      brushRange = [x, x];
    }
  }

  function handleWindowMouseMove(event) {
    let px = getPx(event);
    px = Math.min(Math.max(px, 0), $width);
    const x = $xScale.invert(px);
    if (enableBrushZoom && brushPixels && brushRange) {
      brushPixels[1] = px;
      brushRange[1] = x;
    }
  }

  function handleMouseUp() {
    if (enableBrushZoom && brushPixels && brushRange) {
      const pixelsDistance = Math.abs(brushPixels[1] - brushPixels[0]); // Prevent accidental clicks.
      if (pixelsDistance >= 5) {
        const start = new Date(Math.min(...brushRange));
        const end = new Date(Math.max(...brushRange));
        dispatch("changeDomainX", [start, end]);
      }
    }
    brushRange = brushPixels = undefined;
  }

  function handleWheel(event) {
    if (enableWheelZoom) {
      event.preventDefault();
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        // Horizontal scrolling
        const [start, end] = $xScale.domain();
        const timePerPx = (end.getTime() - start.getTime()) / $width;
        const newStart = new Date(start.getTime() + event.deltaX * timePerPx);
        const newEnd = new Date(end.getTime() + event.deltaX * timePerPx);
        dispatch("changeDomainX", [newStart, newEnd]);
      } else if (event.deltaY > 0.5 || event.deltaY < -0.5) {
        // Vertical or pinch scrollling
        const factor = Math.exp(event.deltaY / 100);
        const [start, end] = $xScale.domain();
        const center = $xScale.invert(getPx(event)).getTime();
        const newStart = new Date(center + (start.getTime() - center) * factor);
        const newEnd = new Date(center + (end.getTime() - center) * factor);
        dispatch("changeDomainX", [newStart, newEnd]);
      }
    }
  }
</script>

<svelte:window
  on:mousemove={handleWindowMouseMove}
  on:mouseup={handleMouseUp}
/>

{#if enableBrushZoom}
  <BrushArea {brushPixels} />
{/if}

<!-- svelte-ignore a11y-no-static-element-interactions -->
<rect
  width={$width}
  height={$height}
  fill="transparent"
  class:cursor-crosshair={enableBrushZoom}
  on:mousedown={handleMouseDown}
  on:mouseup={handleMouseUp}
  on:wheel={handleWheel}
/>
